import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import FileUpload from '../../components/common/FileUpload';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, FileText, Download, Eye, Search, Calendar, Users, Plus, Trash2, X } from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';

const MeetingMinutes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [searchQuery, setSearchQuery] = useState('');
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [minutesForm, setMinutesForm] = useState({
    title: '',
    meeting_date: '',
    attendees: '',
    file_url: '',
    status: 'draft',
    notes: ''
  });
  const [savingMinutes, setSavingMinutes] = useState(false);

  useEffect(() => {
    fetchMinutes();
  }, []);

  const fetchMinutes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/executive/meeting-minutes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMinutes(response.data || []);
    } catch (error) {
      console.error('Error fetching minutes:', error);
      toast({ title: 'Error', description: 'Failed to load meeting minutes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMinutes = async () => {
    if (!minutesForm.title || !minutesForm.meeting_date) {
      toast({ title: 'Missing fields', description: 'Title and meeting date are required', variant: 'destructive' });
      return;
    }
    setSavingMinutes(true);
    try {
      const response = await axios.post(`${backendUrl}/api/executive/meeting-minutes`, minutesForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast({ title: 'Minutes Created', description: 'Meeting minutes have been added' });
      setMinutes(prev => [response.data, ...prev]);
      setMinutesForm({ title: '', meeting_date: '', attendees: '', file_url: '', status: 'draft', notes: '' });
      setShowAddDialog(false);
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to create minutes', variant: 'destructive' });
    } finally {
      setSavingMinutes(false);
    }
  };

  const handleApproveMinutes = async (minutesId) => {
    try {
      await axios.put(`${backendUrl}/api/executive/meeting-minutes/${minutesId}`, { status: 'approved' }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMinutes(prev => prev.map(m => m.id === minutesId ? { ...m, status: 'approved' } : m));
      toast({ title: 'Approved', description: 'Minutes marked as approved' });
    } catch (error) {
      toast({ title: 'Failed', description: 'Failed to approve minutes', variant: 'destructive' });
    }
  };

  const handleDeleteMinutes = async (minutesId) => {
    if (!window.confirm('Are you sure you want to archive these minutes? They can be restored later.')) return;
    try {
      await axios.delete(`${backendUrl}/api/executive/meeting-minutes/${minutesId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMinutes(prev => prev.filter(m => m.id !== minutesId));
      toast({ title: 'Archived', description: 'Meeting minutes archived' });
    } catch (error) {
      toast({ title: 'Failed', description: 'Failed to delete minutes', variant: 'destructive' });
    }
  };

  const filteredMinutes = minutes.filter(minute =>
    minute.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#2c3e7d] text-white py-16">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/executive')}
            className="text-white hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Executive Members
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">
              MEETING MINUTES
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
            <p className="mt-4 text-lg text-white/90">
              Access and manage executive committee meeting minutes
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        {/* Search and Upload */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 max-w-5xl mx-auto">
          <div className="relative flex-1 w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search meeting minutes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button className="bg-[#2c3e7d] hover:bg-[#1e2a5a] w-full md:w-auto" onClick={() => setShowAddDialog(true)}>
            <Plus size={18} className="mr-2" />
            Upload Minutes
          </Button>
        </div>

        {/* Minutes List */}
        <div className="max-w-5xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Meeting Minutes Archive ({filteredMinutes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : filteredMinutes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-gray-600">No meeting minutes found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMinutes.map((minute) => (
                    <div
                      key={minute.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText size={24} className="text-[#2c3e7d]" />
                          <div>
                            <h4 className="font-semibold text-gray-900">{minute.title}</h4>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {new Date(minute.meeting_date).toLocaleDateString()}
                              </span>
                              {minute.attendees && (
                                <span className="flex items-center gap-1">
                                  <Users size={12} />
                                  {minute.attendees}
                                </span>
                              )}
                              <span>• By {minute.uploaded_by_name}</span>
                            </div>
                          </div>
                        </div>
                        <span
                          className={`inline-block px-2 py-1 text-xs rounded-full ${
                            minute.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {minute.status === 'approved' ? 'Approved' : 'Draft'}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        {minute.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveMinutes(minute.id)}
                            className="hover:bg-green-600 hover:text-white"
                          >
                            Approve
                          </Button>
                        )}
                        {minute.file_url && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(minute.file_url, '_blank')}
                            >
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(minute.file_url, '_blank')}
                            >
                              <Download size={16} className="mr-1" />
                              Download
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteMinutes(minute.id)}
                          className="hover:bg-red-600 hover:text-white"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <div className="mt-8 max-w-5xl mx-auto">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FileText className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Meeting Minutes Guidelines</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    All meeting minutes should be submitted within 7 days of the meeting. Minutes must be reviewed 
                    and approved by the committee before being marked as "Approved".
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Minutes Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Meeting Minutes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={minutesForm.title}
                onChange={(e) => setMinutesForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Meeting title..."
              />
            </div>
            <div>
              <Label>Meeting Date *</Label>
              <Input
                type="date"
                value={minutesForm.meeting_date}
                onChange={(e) => setMinutesForm(prev => ({ ...prev, meeting_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Attendees</Label>
              <Input
                value={minutesForm.attendees}
                onChange={(e) => setMinutesForm(prev => ({ ...prev, attendees: e.target.value }))}
                placeholder="Attendee names..."
              />
            </div>
            <div>
              <FileUpload
                folder="meeting-minutes"
                label="Minutes Document (PDF)"
                currentUrl={minutesForm.file_url}
                onUpload={(url) => setMinutesForm(prev => ({ ...prev, file_url: url }))}
                onClear={() => setMinutesForm(prev => ({ ...prev, file_url: '' }))}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={minutesForm.status}
                onValueChange={(v) => setMinutesForm(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={minutesForm.notes}
                onChange={(e) => setMinutesForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMinutes} className="bg-[#2c3e7d]" disabled={savingMinutes}>
              {savingMinutes ? 'Creating...' : 'Create Minutes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingMinutes;